import type { CrossMarketStatus, KrMarketStatus } from '../../services/crossMarketApi';

type KrReadiness = NonNullable<KrMarketStatus['live_readiness']>;

export const BLOCKER_LABELS: Record<string, string> = {
  kr_toss_validated_watchlist_missing: 'Toss KR 검증 watchlist 없음',
  kr_watchlist_display_names_missing: '국내 종목명 메타데이터 부족',
  toss_live_market_not_kr: 'TOSS_LIVE_MARKET이 KR이 아님',
  existing_kr_holdings_not_adopted: '기존 Toss 보유주가 편입/제외로 분류되지 않음',
};

export function watchlistValue(readiness?: KrReadiness) {
  return readiness ? `${readiness.watchlist.count}개 / Toss` : '데이터 없음';
}

export function watchlistDetail(readiness?: KrReadiness) {
  if (!readiness) return '데이터 없음';
  return `${readiness.watchlist.source} · ${readiness.watchlist.count}개 · 이름 ${readiness.watchlist.display_name_count}개`;
}

export function ownershipDetail(readiness?: KrReadiness) {
  if (!readiness) return '데이터 없음';
  const own = readiness.ownership;
  return `관찰 ${own.external_observed} · 편입 ${own.managed_existing} · 제외 ${own.excluded}`;
}

export function sampleDetail(kr?: KrMarketStatus) {
  if (!kr) return '데이터 없음';
  return `판단 ${kr.decisions} · 주문 ${kr.orders} · 체결 ${kr.fills}`;
}

export function gateDetail(kr?: KrMarketStatus) {
  if (!kr?.performance_gate) return kr?.performance_gate_passed ? '통과' : '데이터 없음';
  const gate = kr.performance_gate;
  const sellMin = gate.thresholds?.min_sell ?? 10;
  return [
    gate.passed ? '통과' : '표본 미달',
    `run ${gate.qualified_run_count}`,
    `BUY ${gate.totals.BUY}`,
    `SELL ${gate.totals.SELL}/${sellMin}`,
    `fill ${gate.totals.fills}`,
  ].join(' · ');
}

export function shadowDetail(kr?: KrMarketStatus) {
  const shadow = kr?.live_shadow;
  if (!shadow) return '데이터 없음';
  return shadow.ready ? `저장 ${shadow.recent_count}건 · ${shadow.latest_shadow_at || '시간 없음'}` : '대기 · 저장 데이터 없음';
}

export function liveOrderDetail(kr?: KrMarketStatus) {
  const orders = kr?.live_summary?.orders;
  if (!orders) return '데이터 없음';
  return `최근 ${orders.count}건 · open ${orders.open_count} · filled ${orders.filled_count}`;
}

export function livePnlDetail(kr?: KrMarketStatus) {
  const holdings = kr?.live_summary?.holdings;
  if (!holdings) return '데이터 없음';
  return [
    `보유 ${holdings.count}개`,
    `평가 ${krw(holdings.total_market_value)}`,
    `손익 ${krw(holdings.total_unrealized_pl)}`,
  ].join(' · ');
}

export function shadowLiveDetail(kr?: KrMarketStatus) {
  const compare = kr?.live_summary?.shadow_live_comparison;
  if (!compare) return '데이터 없음';
  if (compare.status === 'no_live_orders') return '비교할 live 주문 없음';
  return compare.alert_required ? `알림 필요 · ${compare.divergent_symbols.join(', ')}` : '차이 없음';
}

export function switchDetail(data: CrossMarketStatus | null) {
  if (!data) return '데이터 없음';
  const safety = data.safety;
  return safety.live_trading_enabled || safety.toss_kr_live_enabled ? '확인 필요' : '잠금';
}

export function loopValue(stage?: string) {
  if (!stage) return '데이터 없음';
  return stage === 'replay_baseline' ? 'Replay/Paper' : stage;
}

function krw(value?: number) {
  if (value === undefined || value === null) return '데이터 없음';
  return `${Math.round(value).toLocaleString('ko-KR')}원`;
}
